import { memo, useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { callSendOnetimePasswordMailApi, callCheckMailAddressDuplicateApi } from 'requests/RequestUser';
import { RoutePath } from 'consts/RoutePath';
import { isDevelop } from 'consts/Env';
import { useStore } from 'store/StoreContext';
import { saveGenerator } from 'store/forgot-password/ForgotPasswordData';
import classes from './override.module.scss';
import { CircularProgress } from '@material-ui/core';
import { HttpStatus } from 'consts/HttpStatus';
import { ReactError } from 'consts/ReactError';
import { Validator } from 'validators/Validator';
import { convertFullToHalf } from 'common/CharacterConverter';

type Props = {};

export const ForgotPasswordTemplate = memo((_props: Props) => {
	const history = useHistory();
	const [store, dispatch] = useStore(); // eslint-disable-line
	const [formData, setFormData] = useState({ mailaddress_1: '' });
	const [formErrors, setFormErrors] = useState({ isNotRobot: '', mailaddress_1: '' });
	const isFormError = !!Object.values(formErrors).find((e) => e !== '');
	const [actionError, setActionError] = useState('');
	const isActionError = actionError !== '';
	const [isCompleteButtonDisabled, setIsCompleteButtonDisabled] = useState(!isDevelop);
	const [processLoading, setProcessLoading] = useState(false);

	// recaptcha
	const recaptchaRef = useRef(null);
	const onRecaptchaChange = (value) => {
		const isError = value == null || value === '';
		setIsCompleteButtonDisabled(true);
		if (!isError) {
			setFormErrors({ ...formErrors, isNotRobot: '' });
			setIsCompleteButtonDisabled(false);
		}
	};

	// メールアドレス入力欄のonChangeで呼ぶ
	const handleChangeValue = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFormData({ ...formData, [event.target.name]: event.target.value });
	};
	const handleBlurConvertFullToHalfValue = (event: React.FocusEvent<HTMLInputElement>) => {
		setFormData((prev) => ({ ...prev, [event.target.name]: convertFullToHalf(event.target.value) }));
	};

	const reset = () => {
		setFormErrors((prev) => ({ ...prev, mailaddress_1: '' }));
		setFormErrors((prev) => ({ ...prev, isNotRobot: '' }));
		setActionError('');
		setFormData({ mailaddress_1: '' });
	};

	// 仮登録メールを送信ボタンのonClickで呼ぶ
	const sendMail = async () => {
		setProcessLoading(true);

		setFormErrors((prev) => ({ ...prev, mailaddress_1: '' }));
		setFormErrors((prev) => ({ ...prev, isNotRobot: '' }));
		setActionError('');

		// メールアドレス確認
		const validator = new Validator(formData.mailaddress_1);
		const mailaddressError = validator.require().charTypeEmail().end();
		if (!!mailaddressError) {
			setFormErrors((prev) => ({ ...prev, mailaddress_1: mailaddressError }));
			setProcessLoading(false);
			return;
		}

		// ロボット確認
		if (!isDevelop) {
			const recaptchaValue = recaptchaRef.current.getValue();
			const isError = recaptchaValue == null || recaptchaValue === '';
			if (isError) {
				setFormErrors((prev) => ({ ...prev, isNotRobot: '「私はロボットではありません」にチェックをしてください。' }));
				setProcessLoading(false);
				return;
			}
		}

		// メールアドレス重複確認
		const checkMailResult = await callCheckMailAddressDuplicateApi({
			mailaddress: formData.mailaddress_1,
		});
		if (checkMailResult.status !== HttpStatus.OK) {
			setActionError(ReactError.E9999);
			history.push(RoutePath.ERROR);
			return;
		}
		if (!checkMailResult.data.exist) {
			setFormErrors((prev) => ({ ...prev, mailaddress_1: 'このメールアドレスは登録されていません' }));
			setProcessLoading(false);
			return;
		}

		// メール送信API
		const sendMailResult = await callSendOnetimePasswordMailApi({
			mailaddress: formData.mailaddress_1,
			send_type: 3,
		});
		if (sendMailResult.status !== HttpStatus.OK) {
			setActionError(ReactError.E9999);
			history.push(RoutePath.ERROR);
			return;
		}

		saveGenerator(dispatch, { mailaddress: formData.mailaddress_1 });

		// 次のページへ
		history.push(RoutePath.FORGOT_PASSWORD_CONFIRM);
	};

	const scrollRef = useRef(null);
	useEffect(() => {
		scrollRef.current.scrollIntoView();
		document.title = 'GakkenIDパスワード忘れ｜メールアドレス入力';
	}, []);

	const errorRef = useRef(null);
	useEffect(() => {
		if (isFormError || isActionError) {
			errorRef.current.scrollIntoView();
		}
	}, [formErrors, actionError]);

	return (
		<>
			<section className="section">
				<div className="section-in" ref={scrollRef}>
					<div className="section-head">パスワード忘れ</div>

					{isFormError || isActionError ? (
						<div className="error-box" ref={errorRef}>
							{isActionError ? <p>{actionError}</p> : null}
							{isFormError ? (
								<>
									<p>※下記項目は全て必須となります。</p>
									<div className="error-box-in">
										{formErrors.mailaddress_1 !== '' ? <p>【メールアドレス】{formErrors.mailaddress_1}</p> : null}
										{formErrors.isNotRobot !== '' ? <p>【ロボット確認】{formErrors.isNotRobot}</p> : null}
									</div>
								</>
							) : null}
						</div>
					) : null}

					<div className="form-box">
						<div className="form-head">
							<label htmlFor="">登録のメールアドレス</label>
						</div>
						<div className="form-input">
							<input
								type="text"
								name="mailaddress_1"
								value={formData.mailaddress_1}
								onChange={handleChangeValue}
								onBlur={handleBlurConvertFullToHalfValue}
							/>
						</div>
						<p className="error">{formErrors.mailaddress_1}</p>
					</div>

					{!isDevelop ? (
						<div className="recaptcha-box">
							<p>機械による不正登録を防ぐために『私はロボットではありません』にチェックをしてください。</p>
							<div className="recaptcha">
								<ReCAPTCHA
									ref={recaptchaRef}
									onChange={onRecaptchaChange}
									sitekey={process.env.REACT_APP_RECAPTCHA_DATA_SITEKEY}
								/>
							</div>
						</div>
					) : null}

					<div className="btn-box">
						{processLoading ? (
							<CircularProgress />
						) : (
							<>
								<div className="btn wh">
									<button onClick={reset}>リセット</button>
								</div>
								<div className="btn gr">
									<button
										className={isCompleteButtonDisabled ? classes.disabled : ''}
										onClick={sendMail}
										disabled={isCompleteButtonDisabled}
									>
										パスワードをリクエストする
									</button>
								</div>
							</>
						)}
					</div>
				</div>
			</section>
		</>
	);
});
